import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  DollarSign,
  Clock,
  Shield,
  Users,
  TrendingUp,
  Award,
  CheckCircle,
  Target,
  Star,
  Circle
} from 'lucide-react';

// Icon mapper to convert icon names to components
const getIconComponent = (iconName) => {
  const iconMap = {
    'dollar-sign': DollarSign,
    'clock': Clock,
    'shield-check': Shield,
    'shield': Shield,
    'target': Target,
    'users': Users,
    'trending-up': TrendingUp,
    'award': Award,
    'star': Star,
    'check-circle': CheckCircle,
    'circle': Circle
  };
  return iconMap[iconName] || Circle;
};

export const BenefitsSection = ({ content, onPostProjectClick }) => {
  const navigate = useNavigate();
  // Default data for fallback
  const defaultData = {
    title: 'Benefits for Everyone',
    subtitle: 'Our bidding platform creates value for both homeowners seeking services and professionals looking to grow their business. Everyone wins when competition drives excellence.',
    homeowner_section: {
      badge: 'For Homeowners',
      title: 'Get Better Results for Less',
      subtitle: 'Why settle for the first quote when you can compare multiple competitive offers?',
      benefits: [
        {
          icon: 'dollar-sign',
          title: 'Save 15-30% on Average',
          description: 'Competitive bidding drives prices down while maintaining quality standards.'
        },
        {
          icon: 'clock',
          title: 'Faster Project Completion',
          description: 'Multiple providers competing means faster response times and project starts.'
        },
        {
          icon: 'shield-check',
          title: 'Verified Professionals Only',
          description: 'All service providers are licensed, insured, and background-checked.'
        },
        {
          icon: 'target',
          title: 'Perfect Match Guarantee',
          description: 'Our smart matching ensures you get providers suited for your specific needs.'
        }
      ],
      cta: {
        text: 'Post Your Project',
        url: '/'
      }
    },
    provider_section: {
      badge: 'For Service Providers',
      title: 'Win More Quality Projects',
      subtitle: 'Connect with motivated customers and grow your business through fair competition.',
      benefits: [
        {
          icon: 'users',
          title: 'Access Quality Leads',
          description: 'Connect with serious homeowners ready to hire for their projects.'
        },
        {
          icon: 'trending-up',
          title: 'Grow Your Business',
          description: 'Expand your customer base and increase revenue through our platform.'
        },
        {
          icon: 'award',
          title: 'Fair Competition',
          description: 'Compete on merit, not just who responds first. Quality work gets rewarded.'
        },
        {
          icon: 'star',
          title: 'Build Reputation',
          description: 'Earn reviews and build your profile to attract more high-value projects.'
        }
      ],
      cta: {
        text: 'Join as Provider',
        url: '/signup?role=provider'
      }
    }
  };

  // Use content from API or fallback to default
  // Ensure we have the complete structure by merging with defaults
  // Backend may use 'homeowners' and 'providers' (without _section suffix)
  const homeownerData = content?.homeowners || content?.homeowner_section;
  const providerData = content?.providers || content?.provider_section;

  const benefitsData = {
    title: content?.title || defaultData.title,
    subtitle: content?.subtitle || defaultData.subtitle,
    homeowner_section: {
      badge: homeownerData?.badge || defaultData.homeowner_section.badge,
      title: homeownerData?.title || defaultData.homeowner_section.title,
      subtitle: homeownerData?.subtitle || defaultData.homeowner_section.subtitle,
      benefits: homeownerData?.benefits || defaultData.homeowner_section.benefits,
      cta: {
        text: homeownerData?.cta?.text || defaultData.homeowner_section.cta.text,
        url: homeownerData?.cta?.url || defaultData.homeowner_section.cta.url
      }
    },
    provider_section: {
      badge: providerData?.badge || defaultData.provider_section.badge,
      title: providerData?.title || defaultData.provider_section.title,
      subtitle: providerData?.subtitle || defaultData.provider_section.subtitle,
      benefits: providerData?.benefits || defaultData.provider_section.benefits,
      cta: {
        text: providerData?.cta?.text || defaultData.provider_section.cta.text,
        url: providerData?.cta?.url || defaultData.provider_section.cta.url
      }
    }
  };

  // Color schemes for benefits (cycles through these)
  const colorSchemes = [
    { color: "text-green-600", bgColor: "bg-green-50" },
    { color: "text-blue-600", bgColor: "bg-blue-50" },
    { color: "text-purple-600", bgColor: "bg-purple-50" },
    { color: "text-emerald-600", bgColor: "bg-emerald-50" },
    { color: "text-orange-600", bgColor: "bg-orange-50" },
    { color: "text-teal-600", bgColor: "bg-teal-50" },
    { color: "text-indigo-600", bgColor: "bg-indigo-50" },
    { color: "text-pink-600", bgColor: "bg-pink-50" }
  ];

  return (
    <section id="benefits" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <span>Win-Win Platform</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {benefitsData.title.split(' ').slice(0, -1).join(' ')}{' '}
            <span className="text-emerald-600">{benefitsData.title.split(' ').slice(-1)[0]}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {benefitsData.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Customer Benefits */}
          <div>
            <div className="text-center mb-8">
              <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Users size={16} />
                <span>{benefitsData.homeowner_section.badge}</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {benefitsData.homeowner_section.title}
              </h3>
              <p className="text-gray-600">
                {benefitsData.homeowner_section.subtitle}
              </p>
            </div>

            <div className="space-y-6">
              {benefitsData.homeowner_section.benefits.map((benefit, index) => {
                const IconComponent = getIconComponent(benefit.icon);
                const colors = colorSchemes[index % colorSchemes.length];

                return (
                  <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className={`${colors.bgColor} p-3 rounded-xl flex-shrink-0`}>
                          <IconComponent className={colors.color} size={24} />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-2">
                            {benefit.title}
                          </h4>
                          <p className="text-gray-600">
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={() => {
                  const ctaUrl = benefitsData.homeowner_section.cta.url;
                  if (ctaUrl === '/' || !ctaUrl) {
                    if (onPostProjectClick) onPostProjectClick();
                  } else {
                    navigate(ctaUrl);
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105"
              >
                {benefitsData.homeowner_section.cta.text}
              </button>
            </div>
          </div>

          {/* Provider Benefits */}
          <div>
            <div className="text-center mb-8">
              <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Award size={16} />
                <span>{benefitsData.provider_section.badge}</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {benefitsData.provider_section.title}
              </h3>
              <p className="text-gray-600">
                {benefitsData.provider_section.subtitle}
              </p>
            </div>

            <div className="space-y-6">
              {benefitsData.provider_section.benefits.map((benefit, index) => {
                const IconComponent = getIconComponent(benefit.icon);
                const colors = colorSchemes[(index + 4) % colorSchemes.length];

                return (
                  <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-0 shadow-md">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className={`${colors.bgColor} p-3 rounded-xl flex-shrink-0`}>
                          <IconComponent className={colors.color} size={24} />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-2">
                            {benefit.title}
                          </h4>
                          <p className="text-gray-600">
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={() => navigate('/signup?role=provider')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105"
              >
                {benefitsData.provider_section.cta.text}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-20 bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Proven Results
            </h3>
            <p className="text-gray-600">
              See why our platform is trusted by thousands of homeowners and service providers.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">18%</div>
              <div className="text-sm text-gray-600">Average Savings</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">4.2</div>
              <div className="text-sm text-gray-600">Bids per Project</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">24hrs</div>
              <div className="text-sm text-gray-600">Avg Response Time</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">96%</div>
              <div className="text-sm text-gray-600">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};